import React from 'react';
import { Button } from '../../../ui/atoms/Button';
import type { ResumeData } from '../resume.types';

const PROFILE_IMAGE_URL = '/me.jpg';

type HeroSectionProps = {
  data: Pick<ResumeData, 'name' | 'title' | 'tagline' | 'profile' | 'keyPoints' | 'contact' | 'stats'>;
  resumePdfUrl: string;
};

export function HeroSection({ data, resumePdfUrl }: HeroSectionProps) {
  return (
    <section
      id="home"
      className="relative overflow-hidden px-4 pb-14 pt-10 md:pb-16 md:pt-14"
      aria-labelledby="hero-heading"
    >
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-50 via-white to-stone-50 dark:from-stone-900 dark:via-stone-900 dark:to-stone-800"
        aria-hidden
      />
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col items-center gap-8 md:flex-row md:items-start md:gap-10">
          <div
            className="shrink-0 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0ms' }}
          >
            <img
              src={PROFILE_IMAGE_URL}
              alt="Alex Appadurai"
              className="h-32 w-32 rounded-2xl object-cover shadow-lg ring-2 ring-white/80 ring-offset-2 ring-offset-emerald-100 transition-shadow duration-300 hover:shadow-xl md:h-40 md:w-40"
            />
          </div>
          <div className="min-w-0 flex-1 text-center md:text-left">
            <h1
              id="hero-heading"
              className="font-display text-3xl font-bold tracking-tight text-stone-900 opacity-0 animate-fade-in-up dark:text-white md:text-4xl lg:text-5xl"
              style={{ animationDelay: '80ms' }}
            >
              {data.name}
            </h1>
            <p
              className="mt-2 font-display text-lg text-emerald-700 opacity-0 animate-fade-in-up dark:text-emerald-400 md:text-xl"
              style={{ animationDelay: '160ms' }}
            >
              {data.title}
            </p>
            <p
              className="mt-3 text-base text-stone-600 opacity-0 animate-fade-in-up dark:text-stone-400 md:text-lg"
              style={{ animationDelay: '240ms' }}
            >
              {data.tagline}
            </p>
            {data.keyPoints && data.keyPoints.length > 0 && (
              <div
                className="mt-3 flex flex-wrap gap-2 opacity-0 animate-fade-in-up"
                style={{ animationDelay: '280ms' }}
              >
                {data.keyPoints.map((point) => (
                  <span
                    key={point}
                    className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm dark:bg-emerald-500 dark:text-stone-900"
                  >
                    {point}
                  </span>
                ))}
              </div>
            )}
            <p
              className="mt-4 max-w-2xl text-stone-600 leading-relaxed opacity-0 animate-fade-in-up dark:text-stone-400 md:max-w-none"
              style={{ animationDelay: '320ms' }}
            >
              {data.profile}
            </p>
            <div
              className="mt-6 flex flex-wrap items-center justify-center gap-3 opacity-0 animate-fade-in-up md:justify-start"
              style={{ animationDelay: '400ms' }}
            >
              <Button href={resumePdfUrl} aria-label="Download resume PDF">
                Download resume (PDF)
              </Button>
              {data.contact.linkedIn && (
                <Button
                  href={data.contact.linkedIn}
                  variant="outline"
                  aria-label="Open LinkedIn profile"
                >
                  LinkedIn
                </Button>
              )}
            </div>
            <div
              className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-stone-500 opacity-0 animate-fade-in-up dark:text-stone-400 md:justify-start"
              style={{ animationDelay: '480ms' }}
            >
              <a
                href={`mailto:${data.contact.email}`}
                className="hover:text-emerald-600 hover:underline dark:hover:text-emerald-400"
              >
                {data.contact.email}
              </a>
              <a
                href={`tel:${data.contact.phone.replace(/\s/g, '')}`}
                className="hover:text-emerald-600 hover:underline dark:hover:text-emerald-400"
              >
                {data.contact.phone}
              </a>
              <span>{data.contact.location}</span>
            </div>
          </div>
        </div>

        <div
          className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '560ms' }}
          aria-label="Career highlights"
        >
          {data.stats.map((stat, i) => (
            <div
              key={stat.label}
              className="rounded-xl border border-stone-200/80 bg-white/80 px-3 py-2.5 text-center shadow-sm backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-md dark:border-stone-600 dark:bg-stone-800/80"
              style={{ animationDelay: `${620 + i * 50}ms` }}
            >
              <span className="font-display text-xl font-bold text-emerald-600 dark:text-emerald-400 md:text-2xl">
                {stat.value}
              </span>
              <span className="mt-0.5 block text-xs font-medium uppercase tracking-wider text-stone-500 dark:text-stone-400">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
